"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Wrench,
} from "lucide-react";
import type { CopilotToolCall } from "@/lib/copilot/types";

const SKILL_LABELS: Record<string, string> = {
  navigateTab: "Navegar",
  listMyRestaurants: "Listar restaurantes",
  getAnalytics: "Consultar analítica",
};

export function CopilotToolCallCard({ tc }: { tc: CopilotToolCall }) {
  const [expanded, setExpanded] = useState(false);
  const label = SKILL_LABELS[tc.name] ?? tc.name;
  const elapsed = tc.endedAt
    ? `${Math.max(0, tc.endedAt - tc.startedAt)} ms`
    : null;

  const statusIcon =
    tc.status === "running" ? (
      <Loader2 className="h-3 w-3 animate-spin text-primary" />
    ) : tc.status === "success" ? (
      <Check className="h-3 w-3 text-emerald-500" />
    ) : (
      <AlertCircle className="h-3 w-3 text-rose-500" />
    );

  const accent =
    tc.status === "running"
      ? "border-primary/40 bg-primary/5"
      : tc.status === "success"
        ? "border-emerald-500/30 bg-emerald-500/5"
        : "border-rose-500/30 bg-rose-500/5";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.15 }}
      className={`rounded-lg border px-2.5 py-1.5 text-[11px] ${accent}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-1.5 text-left"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
        )}
        <Wrench className="h-3 w-3 text-muted-foreground" />
        <span className="font-medium">{label}</span>
        <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
          {elapsed && <span>{elapsed}</span>}
          {statusIcon}
        </span>
      </button>

      {expanded && (
        <div className="mt-1.5 space-y-1.5 border-t pt-1.5">
          {Object.keys(tc.arguments).length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-wide text-muted-foreground">
                Args
              </p>
              <pre className="overflow-x-auto rounded bg-muted/60 px-1.5 py-1 text-[10px] [scrollbar-width:thin]">
                {JSON.stringify(tc.arguments, null, 2)}
              </pre>
            </div>
          )}
          {tc.status === "success" && tc.result !== undefined && (
            <div>
              <p className="text-[9px] uppercase tracking-wide text-muted-foreground">
                Resultado
              </p>
              <pre className="max-h-40 overflow-auto rounded bg-muted/60 px-1.5 py-1 text-[10px] [scrollbar-width:thin]">
                {JSON.stringify(tc.result, null, 2)}
              </pre>
            </div>
          )}
          {tc.status === "error" && tc.error && (
            <p className="rounded bg-destructive/10 px-1.5 py-1 text-[10px] text-destructive">
              {tc.error}
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
}
